import { Metrics } from './types';

/**
 * PROVIDER PRECEDENCE — the single source of truth for "which provider's number do we show".
 *
 * GA4 is PRIMARY. Clarity is a FALLBACK, used only where GA4 cannot measure something at all.
 *
 * Why this file exists: both providers write into the same metrics dictionary, so a single row
 * can carry `sessions` (GA4) AND `total_session_count` (Clarity) for the same day. Deciding
 * between them with an inline `??` at each call site meant the precedence lived in a dozen
 * places, and one wrong ordering would silently show Clarity's number while the page claimed
 * GA4's. The order is declared ONCE, here.
 *
 * The two providers do NOT agree, and should not be expected to: Clarity counts bot sessions
 * that GA4 filters, and ad-blockers suppress GA4 more than Clarity. Mixing them within a single
 * measure produces a number belonging to neither, which is why a chain resolves to exactly one
 * source rather than summing or averaging across them.
 */

/** A canonical measure, and the provider metrics that can supply it — best source first. */
export const MetricChains = {
  /** GA4 sessions; Clarity's session count only when GA4 has nothing. */
  sessions: [Metrics.sessions, Metrics.totalSessionCount],

  /** GA4 active users; Clarity's distinct users as fallback. */
  users: [Metrics.activeUsers, Metrics.distinctUserCount],

  /**
   * GA4 page views; Clarity's own count as fallback.
   *
   * These are two keys on purpose. Clarity was previously written into GA4's `screen_page_views`,
   * which made the backend SUM them and report roughly double. Note `pages_per_session` is a
   * RATIO and is deliberately absent here — it is not a page-view count.
   */
  pageViews: [Metrics.screenPageViews, Metrics.clarityPageViews],

  /** GA4's engagement duration; Clarity's engagement time only if GA4 is absent. */
  engagement: [
    Metrics.userEngagementDuration,
    Metrics.averageSessionDuration,
    Metrics.averageEngagementTime,
    Metrics.activeEngagementTime,
  ],

  /** GA4 only. */
  newUsers: [Metrics.newUsers],
  conversions: [Metrics.conversions],
  bounceRate: [Metrics.bounceRate],
  engagementRate: [Metrics.engagementRate],

  /** First-party only — neither analytics provider knows about a signup. */
  leads: [Metrics.leads],

  // ---- Clarity-only. GA4 cannot measure any of these, which is the reason Clarity is here.
  rageClicks: [Metrics.rageClickCount],
  deadClicks: [Metrics.deadClickCount],
  errorClicks: [Metrics.errorClickCount],
  excessiveScroll: [Metrics.excessiveScrollCount],
  quickbacks: [Metrics.quickbackClickCount],
  scriptErrors: [Metrics.scriptErrorCount],
  scrollDepth: [Metrics.averageScrollDepth],
  frustrationRate: [Metrics.sessionsWithMetricPct],
  botSessions: [Metrics.totalBotSessionCount],
} as const;

export type MetricChain = keyof typeof MetricChains;

/** Which provider a metric key belongs to — drives the "source" label in the UI. */
const CLARITY_METRICS = new Set<string>([
  Metrics.totalSessionCount,
  Metrics.clarityPageViews,
  Metrics.distinctUserCount,
  Metrics.totalBotSessionCount,
  Metrics.pagesPerSession,
  Metrics.averageScrollDepth,
  Metrics.averageEngagementTime,
  Metrics.activeEngagementTime,
  Metrics.sessionsWithMetricPct,
  Metrics.rageClickCount,
  Metrics.deadClickCount,
  Metrics.excessiveScrollCount,
  Metrics.quickbackClickCount,
  Metrics.scriptErrorCount,
  Metrics.errorClickCount,
]);

const FIRST_PARTY_METRICS = new Set<string>([Metrics.leads, Metrics.uniqueVisitors]);

export type MetricSource = 'ga4' | 'clarity' | 'first-party';

export function sourceOf(metricKey: string): MetricSource {
  if (FIRST_PARTY_METRICS.has(metricKey)) return 'first-party';
  return CLARITY_METRICS.has(metricKey) ? 'clarity' : 'ga4';
}

export interface Resolved {
  value: number;
  /** The metric key that actually supplied the value. */
  key: string;
  source: MetricSource;
  /** True when GA4 was unavailable and a lower-priority provider answered. */
  isFallback: boolean;
}

/**
 * Resolves a canonical measure from a metrics dictionary, honouring provider precedence.
 *
 * Returns null when NO provider supplied it — deliberately distinct from returning 0, because
 * "nobody measured this" and "this is zero" are different facts and the UI renders them
 * differently ("—" versus "0").
 */
export function resolveMetric(
  metrics: Record<string, number> | undefined,
  chain: MetricChain,
): Resolved | null {
  if (!metrics) return null;

  const keys = MetricChains[chain] as readonly string[];

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]!;
    const value = metrics[key];
    if (value !== undefined) {
      return { value, key, source: sourceOf(key), isFallback: i > 0 };
    }
  }

  return null;
}

/** Convenience for the common "give me the number or null" case. */
export function metricValue(
  metrics: Record<string, number> | undefined,
  chain: MetricChain,
): number | null {
  return resolveMetric(metrics, chain)?.value ?? null;
}

/** Human label for where a number came from, for the UI's provenance line. */
export function sourceLabel(resolved: Resolved | null): string | undefined {
  if (!resolved) return undefined;
  if (resolved.source === 'first-party') return 'First-party';
  if (resolved.source === 'ga4') return 'Google Analytics';
  // Say WHY Clarity is answering — either it is the only source, or GA4 went missing.
  return resolved.isFallback ? 'Clarity (GA4 unavailable)' : 'Microsoft Clarity';
}
